import React, { useState, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const FinalizacaoVendaModal = ({
  open,
  onClose,
  ticket,
  onFinalizar,
  initialVendaConcluida = true,
}) => {
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [vendaConcluida, setVendaConcluida] = useState(true);
  const [valorVenda, setValorVenda] = useState("");
  const [motivoNaoVenda, setMotivoNaoVenda] = useState("");
  const [motivosFinalizacao, setMotivosFinalizacao] = useState([]);
  const [companyMotivos, setCompanyMotivos] = useState([]);
  const [currency, setCurrency] = useState("USD $");
  const [loadingConfig, setLoadingConfig] = useState(true);

  const handleClose = () => {
    onClose();
  };

  useEffect(() => {
    if (open) {
      setVendaConcluida(initialVendaConcluida !== undefined ? Boolean(initialVendaConcluida) : true);
      const prefillVal = ticket?.opportunityValue ?? ticket?.valorVenda ?? "";
      setValorVenda(
        prefillVal !== "" && prefillVal !== null && prefillVal !== undefined
          ? String(prefillVal)
          : ""
      );
      setMotivoNaoVenda(ticket?.motivoNaoVenda || "");
      setLoadingConfig(true);
      Promise.all([
        fetchMotivosFinalizacao(),
        fetchCompanySettings(),
        fetchCurrency(),
      ]).finally(() => setLoadingConfig(false));
    }
  }, [open, initialVendaConcluida, ticket]);

  const fetchMotivosFinalizacao = async () => {
    try {
      const { data } = await api.get("/ticketFinalizationReasons");
      if (Array.isArray(data)) {
        setMotivosFinalizacao(data);
      }
    } catch (err) {
      console.error("Erro ao buscar motivos de finalização:", err);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const { data } = await api.get("/companySettings");
      if (data?.motivosFinalizacao && typeof data.motivosFinalizacao === "string") {
        try {
          const parsed = JSON.parse(data.motivosFinalizacao);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setCompanyMotivos(
              parsed.map((m, idx) =>
                typeof m === "string" ? { id: `comp_${idx}`, name: m } : m
              )
            );
          }
        } catch (e) {
          // ignore parsing error
        }
      }
    } catch (err) {
      console.error("Erro ao buscar configurações da empresa:", err);
    }
  };

  const fetchCurrency = async () => {
    try {
      const { data } = await api.get("/settings");
      const curr = data?.find((s) => s.key === "system_currency");
      if (curr?.value) {
        setCurrency(curr.value);
      }
    } catch (err) {
      console.error("Erro ao buscar moeda do sistema:", err);
    }
  };

  const defaultReasons = [
    { id: "default_1", name: i18n.t("tickets.finalizationModal.defaultReasons.noAnswer") },
    { id: "default_2", name: i18n.t("tickets.finalizationModal.defaultReasons.outOfBudget") },
    { id: "default_3", name: i18n.t("tickets.finalizationModal.defaultReasons.boughtCompetitor") },
    { id: "default_4", name: i18n.t("tickets.finalizationModal.defaultReasons.unqualified") },
    { id: "default_5", name: i18n.t("tickets.finalizationModal.defaultReasons.infoOnly") },
    { id: "default_6", name: i18n.t("tickets.finalizationModal.defaultReasons.other") },
  ];

  const availableReasons =
    motivosFinalizacao.length > 0
      ? motivosFinalizacao
      : companyMotivos.length > 0
      ? companyMotivos
      : defaultReasons;

  const handleSubmit = async () => {
    let finalValorVenda = 0;
    if (vendaConcluida) {
      if (valorVenda !== "" && valorVenda !== null && valorVenda !== undefined) {
        const parsed = parseFloat(valorVenda);
        if (isNaN(parsed)) {
          toast.error(i18n.t("tickets.finalizationModal.errors.provideSaleValue"));
          return;
        }
        finalValorVenda = Math.max(0, parsed);
      } else {
        finalValorVenda = 0;
      }
    } else {
      if (!motivoNaoVenda) {
        toast.error(i18n.t("tickets.finalizationModal.errors.selectReasonNoSale"));
        return;
      }
    }

    const ticketData = {
      status: "closed",
      userId: user?.id || null,
      finalizadoComVenda: Boolean(vendaConcluida),
      valorVenda: vendaConcluida ? finalValorVenda : null,
      motivoNaoVenda: vendaConcluida ? null : motivoNaoVenda,
      motivoFinalizacao: null,
      sendFarewellMessage: false,
    };

    if (onFinalizar) {
      onFinalizar(ticketData);
      handleClose();
    } else {
      try {
        setLoading(true);
        await api.put(`/tickets/${ticket.id}`, ticketData);
        toast.success(i18n.t("tickets.success.finished", "Atención finalizada con éxito"));
        handleClose();
        history.push("/tickets");
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{i18n.t("tickets.finalizationModal.title")}</DialogTitle>
      {loadingConfig ? (
        <DialogContent>
          <Typography>{i18n.t("tickets.finalizationModal.loading")}</Typography>
        </DialogContent>
      ) : (
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">
                  {i18n.t("tickets.finalizationModal.questionConcluded")}
                </FormLabel>
                <RadioGroup
                  value={vendaConcluida}
                  onChange={(e) =>
                    setVendaConcluida(e.target.value === "true")
                  }
                >
                  <FormControlLabel
                    value={true}
                    control={<Radio color="primary" />}
                    label={i18n.t("tickets.finalizationModal.yesConcluded")}
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio color="primary" />}
                    label={i18n.t("tickets.finalizationModal.noConcluded")}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            {vendaConcluida ? (
              <Grid item xs={12}>
                <TextField
                  label={`${i18n.t("tickets.finalizationModal.saleValue")} (${currency})`}
                  type="number"
                  value={valorVenda}
                  onChange={(e) => setValorVenda(e.target.value)}
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  inputProps={{
                    step: "0.01",
                    min: "0",
                  }}
                  placeholder={i18n.t("tickets.finalizationModal.saleValuePlaceholder")}
                  helperText={i18n.t("tickets.finalizationModal.saleValueHelp", "Puede ingresar 0 si la conversión no tuvo costo")}
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  required
                >
                  <InputLabel>{i18n.t("tickets.finalizationModal.reasonNoSale")}</InputLabel>
                  <Select
                    value={motivoNaoVenda}
                    onChange={(e) => setMotivoNaoVenda(e.target.value)}
                    label={i18n.t("tickets.finalizationModal.reasonNoSale")}
                  >
                    <MenuItem value="">
                      <em>{i18n.t("tickets.finalizationModal.selectReason")}</em>
                    </MenuItem>
                    {availableReasons.map((motivo) => (
                      <MenuItem key={motivo.id} value={motivo.name}>
                        {motivo.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={loading}>
          {i18n.t("tickets.finalizationModal.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading || loadingConfig}
        >
          {loading
            ? i18n.t("tickets.finalizationModal.finalizing")
            : i18n.t("tickets.finalizationModal.finalize")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FinalizacaoVendaModal;
